/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import type { DialogProps } from '@mui/material';
import { DialogContent, Box } from '@mui/material';
import React from 'react';
import { CustomDialog, ToolHeader } from '@opengeoweb/shared';
import { NotificationTrigger } from '../NotificationTrigger/NotificationTrigger';

interface ContentDialogProps extends DialogProps {
  open: boolean;
  toggleStatus: () => void;
  title: string;
  children?: React.ReactNode;
  alertBanner?: React.ReactNode;
  onClose?: () => void;
}

const ContentDialog: React.FC<ContentDialogProps> = ({
  open,
  toggleStatus,
  title,
  children,
  onClose,
  alertBanner = null,
  ...other
}: ContentDialogProps) => {
  const handleClose = (): void => {
    if (onClose) {
      onClose();
    } else {
      toggleStatus();
    }
  };

  return (
    <CustomDialog
      hasLayout={false}
      headerActions={[]}
      maxWidth={other.maxWidth ? other.maxWidth : 'lg'}
      onClose={handleClose}
      open={open}
      title={title}
      {...other}
    >
      <ToolHeader onClose={handleClose} size="xs" title={title} />
      <NotificationTrigger />
      {alertBanner !== null && alertBanner}
      <Box
        sx={{
          backgroundColor: 'geowebColors.background.surface',
          overflow: 'auto',
        }}
      >
        <DialogContent sx={{ pt: 2 }}>{children}</DialogContent>
      </Box>
    </CustomDialog>
  );
};

export default ContentDialog;
